import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Link as MuiLink,
  useTheme,
} from "@mui/material";
import {
  Android,
  Business,
  Favorite,
  Groups,
  Security,
  TrendingUp,
} from "@mui/icons-material";
import { PLAY_STORE_URL } from "../config/store";

const COMPANY = "Weps Technology";
const ADDRESS = "H.No. 383, Muzaffarabad, Saharanpur, Uttar Pradesh, India – 247129";

const About = () => {
  const theme = useTheme();

  const cards = [
    {
      icon: TrendingUp,
      title: "Personal money management first",
      body: "Treasure helps you track income and expenses, understand spending, and build clearer money habits—especially useful for students and young professionals.",
    },
    {
      icon: Business,
      title: "Built by Weps Technology",
      body: `We are ${COMPANY}, based in Uttar Pradesh, India. We build useful, reliable apps that respect your privacy and keep everyday money tracking simple.`,
    },
    {
      icon: Favorite,
      title: "Our mission",
      body: "Help you stay on top of your money with a clean, straightforward experience—no clutter, no hidden fees, just clarity.",
    },
    {
      icon: Security,
      title: "Security & privacy",
      body: "We take privacy seriously. We do not sell your personal or financial information for marketing. See our Privacy Policy for how data is handled.",
    },
    {
      icon: Groups,
      title: "Also in Treasure: auction committees",
      body: "Beyond personal tracking, Treasure includes an additional capability to create and manage auction committees and share transparent access with members.",
    },
  ];

  return (
    <Box sx={{ bgcolor: "#f7f5fb", pb: 8 }}>
      <Box
        sx={{
          color: "white",
          background: `linear-gradient(160deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, #7E57C2 100%)`,
          py: { xs: 6, md: 8 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              opacity: 0.95,
              mb: 1.5,
            }}
          >
            About Treasure
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.9rem", md: "2.5rem" },
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              mb: 1.5,
            }}
          >
            About Treasure – Money Management App by Weps Technology
          </Typography>
          <Typography sx={{ opacity: 0.94, lineHeight: 1.7, maxWidth: 560, fontSize: "1.05rem" }}>
            Treasure is a simple money-management app for Android—built to help you track income and expenses,
            understand spending, and stay in control of your month.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: { xs: -3, md: -4 }, px: 2 }}>
        <Stack spacing={2}>
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <Box
                key={card.title}
                sx={{
                  display: "flex",
                  gap: 2,
                  p: { xs: 2.5, md: 3 },
                  bgcolor: "white",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "rgba(94,53,177,0.12)",
                  boxShadow: "0 8px 24px rgba(94,53,177,0.06)",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon />
                </Box>
                <Box>
                  <Typography fontWeight={800} sx={{ mb: 0.75 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {card.body}
                    {card.title === "Security & privacy" && (
                      <>
                        {" "}
                        <MuiLink component={RouterLink} to="/privacy-policy" underline="hover">
                          Privacy Policy
                        </MuiLink>
                        .
                      </>
                    )}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>

        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            bgcolor: "white",
            border: "1px solid",
            borderColor: "rgba(94,53,177,0.12)",
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Typography fontWeight={700} sx={{ mb: 1 }}>
            {COMPANY}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            {ADDRESS}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
            <Button
              component="a"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<Android />}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
            >
              Get Treasure on Google Play
            </Button>
            <Button
              component={RouterLink}
              to="/contact-us"
              variant="outlined"
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
            >
              Contact us
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default About;
